
import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { useSession } from "@supabase/auth-helpers-react";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";
import { containsProfanity } from "@/utils/profanityFilter";

interface PrayerRequestFormProps {
  onSubmit: () => Promise<void>;
  onPreSubmit?: (data: any) => boolean;
  remainingUses?: number;
}

export function PrayerRequestForm({ onSubmit, onPreSubmit, remainingUses = Infinity }: PrayerRequestFormProps) {
  const [content, setContent] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  const session = useSession();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (isSubmitting) return;
    
    // Validate content
    if (!content.trim()) {
      toast({
        title: "Error",
        description: "Please enter a prayer request.",
        variant: "destructive",
      });
      return;
    }
    
    // Check for profanity
    if (containsProfanity(content)) {
      toast({
        title: "Inappropriate Content",
        description: "Please ensure your prayer request does not contain inappropriate language.",
        variant: "destructive",
      });
      return;
    }

    // Check if we have a pre-submit handler, which might limit submissions
    if (onPreSubmit && !onPreSubmit({ content })) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Create the prayer request with a generated title
      const title = `Prayer request ${new Date().toLocaleDateString()}`;
      
      // Create the prayer request
      const { error } = await supabase.from('prayer_requests').insert({
        content,
        title, 
        user_id: session?.user?.id || null,
        is_anonymous: !session?.user?.id,
      });
      
      if (error) throw error;
      
      toast({
        title: "Success",
        description: "Your prayer request has been submitted.",
      });
      
      // Reset form and trigger refresh of prayer requests
      setContent("");
      onSubmit();
    } catch (error) {
      console.error('Error submitting prayer request:', error);
      toast({
        title: "Error",
        description: "Failed to submit prayer request. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card className="p-4 bg-[#151520]/80 backdrop-blur-sm border-[#333344] text-white">
      <form onSubmit={handleSubmit}>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">
              {remainingUses < Infinity 
                ? `Share Your Prayer Request (${remainingUses} free ${remainingUses === 1 ? 'request' : 'requests'} remaining this week)`
                : "Share Your Prayer Request"
              }
            </label>
            <Textarea
              placeholder="Type your prayer request here..."
              value={content}
              onChange={(e) => setContent(e.target.value)}
              className="h-32 bg-[#1A1A30]/80 border-[#333355] text-white"
              disabled={isSubmitting || remainingUses <= 0}
            />
          </div>
          
          <div className="flex justify-end">
            <Button 
              className="bg-blue-700 hover:bg-blue-800"
              disabled={isSubmitting || remainingUses <= 0}
              type="submit"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                "Submit Prayer Request"
              )}
            </Button>
          </div>
        </div>
      </form>
    </Card>
  );
}
